import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { Link, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { newId, OBSERVATION_KINDS, type ObservationKind } from '@gk/contracts'
import { useClub, useEngineState } from '../club/ClubProvider.tsx'
import { OBSERVATION_FOLLOW_UP_DAYS, buildObservationPayload, candidateJobs, formProblem, useAreaObservations, type Observation } from '../data/observations.ts'
import { say, useJobAreas, useJobs } from '../data/plan.ts'
import { formatLocal, localToInstant } from '../lib/time.ts'
import { PhotoField, PhotoStrip } from '../photos/PhotoField.tsx'
import { usePhotoDraft } from '../photos/usePhotos.ts'
import { BackLink, Banner, DisabledHint, Field, PendingBanner, Pill, Section } from '../ui/components.tsx'
import { useAction, useWorkshopAction, useWriteAccess } from '../ui/hooks.ts'
import { scrollToHashWhenSettled } from '../ui/scrollToHash.ts'
import { useAreaOptions, usePersonNames } from './measures/shared.tsx'

// M16-N: the two commands of the part, registered in packages/contracts (COMMANDS) and sync/projectors.ts at Z's close (docs/M16.md rule 8)
const RECORD = 'observation.record@1'
const LINK_JOB = 'observation.link_job@1'

/** The one reading of a kind's word (literal keys, so i18nKeys.test.ts holds each one to the catalogue). */
function kindText(t: (k: string) => string, k: ObservationKind): string {
  switch (k) {
    case 'disease': return t('observe.kind.disease')
    case 'pest': return t('observe.kind.pest')
    case 'damage': return t('observe.kind.damage')
    case 'wet': return t('observe.kind.wet')
    case 'dry': return t('observe.kind.dry')
    default: return t('observe.kind.other')
  }
}

/** "Signaler une observation" (GK-022/23; docs/M16.md N): "j'ai vu ça sur le green 4", with a picture, offline, in ten seconds. A field FACT (the notebook's
 *  own permission): the area pre-selected when the page comes from the area page or the map (`?area=`), else chosen from the course by name and code;
 *  the kind as cards; a word; the photo. Under the form, the area's own observations with their photo, and — for a planner — the follow-up: an ordinary
 *  job of the plan named once on the observation (rule 1). Reached from Today's Terrain row (M16-A), the area page (M16-K) and, in release b, the map. */
export function ObservePage() {
  const { t } = useTranslation()
  const search = useSearch({ strict: false }) as { area?: string; from?: string }
  const { send, site, kiosk } = useClub()
  const engine = useEngineState()
  const access = useWriteAccess('measurement.record')
  // M16a review: a photo is a second fact (attachment.register@1), which the shared workstation may not send (not kiosk-allowed): the field is held there,
  // so no picture is taken only to be refused after the observation applied
  const photo = useWriteAccess('attachment.add')
  const areas = useAreaOptions()
  const { busy, error, run } = useAction()
  const photos = usePhotoDraft()
  const id = useRef(newId())
  const [areaId, setAreaId] = useState(search.area ?? '')
  const [picking, setPicking] = useState(!search.area)
  const [kind, setKind] = useState<ObservationKind | ''>('')
  const [note, setNote] = useState('')
  const [when, setWhen] = useState('')
  const [sent, setSent] = useState<{ id: string; offline: boolean } | null>(null)

  const area = areas?.find((a) => a.id === areaId)
  const problem = formProblem({ areaId: area ? areaId : '', kind, note })
  const fromArea = search.from === 'course' && !!area
  // M16b (Z's close, docs/m16-requests/e.md 10): opened from the map's area popup (`?from=map`), the page's own way back returns to the map on that area
  const fromMap = search.from === 'map' && !!area
  // #observations (Today's "n observations sans suite", the Signaux tab): the page lands on the area's list, under the form (M23: ui/scrollToHash.ts, once settled)
  const listed = !!area && !sent
  useEffect(() => (listed && typeof window !== 'undefined' && window.location.hash === '#observations' ? scrollToHashWhenSettled('observations') : undefined), [listed])

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (problem || !access.allowed) return
    void run(async () => {
      const at = when ? localToInstant(when.slice(0, 10), when.slice(11, 16), site.timezone) : undefined
      const payload = buildObservationPayload(id.current, { areaId, kind, note, ...(at ? { observedAt: at } : {}) })
      const { commandId } = await send(RECORD, payload)
      await photos.commit({ entity_type: 'ops.observation', entity_id: id.current }, [commandId])
      setSent({ id: id.current, offline: engine.online === false || (typeof navigator !== 'undefined' && navigator.onLine === false) })
    })
  }
  const another = () => { id.current = newId(); setKind(''); setNote(''); setWhen(''); setSent(null) }

  if (!areas) return <p>{t('common.loading')}</p>
  const back = fromArea
    ? <BackLink to="/course" search={{ area: areaId } as never}>← {t('observe.backArea')}</BackLink>
    : fromMap
      ? <BackLink to="/map" search={{ area: areaId }}>← {t('observe.backMap')}</BackLink>
      : <BackLink to="/">← {t('common.back')}</BackLink>

  if (sent) {
    return (
      <section className="flex flex-col gap-4" data-testid="observe-sent" data-id={sent.id}>
        {back}
        <h1 className="text-2xl font-bold">{t('observe.title')}</h1>
        <Banner tone={sent.offline ? 'warn' : 'ok'}><span className="text-lg font-bold" data-testid={sent.offline ? 'observe-sent-offline' : 'observe-sent-ok'}>{t(sent.offline ? 'observe.sentOffline' : 'observe.sent')}</span></Banner>
        {area && <p className="text-lg font-semibold" data-testid="observe-sent-area">{area.label}</p>}
        <div data-testid="observe-sent-photos"><PhotoStrip entityId={sent.id} /></div>
        <button type="button" className="btn btn-primary" onClick={another} data-testid="observe-another">{t('observe.another')}</button>
        <Link to="/course" search={{ area: areaId } as never} className="btn" data-testid="observe-see-area">{t('observe.seeArea')}</Link>
        <div id="observations"><AreaObservations areaId={areaId} /></div>
      </section>
    )
  }

  return (
    <section className="flex flex-col gap-4" data-testid="observe-page">
      {back}
      <div>
        <h1 className="text-2xl font-bold">{t('observe.title')}</h1>
        <p className="meta">{t('observe.help')}</p>
      </div>
      {!access.permitted && <Banner tone="warn" testId="observe-not-allowed">{t('observe.notAllowed')}</Banner>}
      {access.readOnlyMode && <Banner tone="warn">{t('platform.readOnly')}</Banner>}
      <form className="flex flex-col gap-4" onSubmit={submit}>
        {area && !picking ? (
          <div className="card flex min-h-14 items-center justify-between gap-3">
            <div className="flex min-w-0 flex-col"><span className="meta">{t('observe.area')}</span><span className="truncate text-lg font-semibold" data-testid="observe-area-line">{area.label}</span></div>
            <button type="button" className="btn shrink-0 whitespace-nowrap !px-4" onClick={() => setPicking(true)} data-testid="observe-area-change">{t('observe.areaChange')}</button>
          </div>
        ) : (
          <Field label={t('observe.area')} htmlFor="observe-area">
            <select id="observe-area" className="input" value={area ? areaId : ''} onChange={(e) => { setAreaId(e.target.value); if (e.target.value) setPicking(false) }} data-testid="observe-area" autoFocus={!area}>
              <option value="">{t('observe.areaPick')}</option>
              {areas.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
            </select>
          </Field>
        )}
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm font-semibold">{t('observe.kindLabel')}</legend>
          <div className="grid grid-cols-2 gap-2" role="group" aria-label={t('observe.kindLabel')}>
            {OBSERVATION_KINDS.map((k) => (
              <button key={k} type="button" className={`btn ${kind === k ? 'btn-primary' : ''}`} aria-pressed={kind === k} onClick={() => setKind(k)} data-testid={`observe-kind-${k}`}>{kindText(t, k)}</button>
            ))}
          </div>
        </fieldset>
        <Field label={kind === 'other' ? t('observe.noteOther') : t('observe.note')} hint={t('observe.noteHint')} htmlFor="observe-note">
          <textarea id="observe-note" className="input" rows={3} maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} data-testid="observe-note" />
        </Field>
        <details className="card">
          <summary className="flex min-h-14 cursor-pointer items-center font-semibold" data-testid="observe-when-toggle">{t('observe.when')}</summary>
          <Field label={t('observe.when')} hint={t('observe.whenHint')} htmlFor="observe-when">
            <input id="observe-when" className="input" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="observe-when" />
          </Field>
        </details>
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-bold">{t('observe.photo')}</h2>
          <p className="meta">{t('observe.photoHint')}</p>
          {kiosk && !photo.permitted && <p className="meta" data-testid="observe-photo-kiosk">{t('observe.photoKiosk')}</p>}
          <PhotoField draft={photos} disabled={!access.allowed || !photo.allowed} />
        </div>
        {error && <Banner tone="error"><span data-testid="observe-error">{error}</span></Banner>}
        <button className="btn btn-primary" disabled={busy || !!problem || !access.allowed} data-testid="observe-submit">{busy ? t('common.saving') : t('observe.send')}</button>
        <DisabledHint show={!!problem && access.allowed} testId="observe-hint">{problem === 'area' ? t('observe.missing.area') : problem === 'kind' ? t('observe.missing.kind') : problem === 'noteLong' ? t('observe.missing.noteLong') : t('observe.missing.note')}</DisabledHint>
        <p className="meta" data-testid="observe-rule">{t('observe.rule', { days: OBSERVATION_FOLLOW_UP_DAYS })}</p>
      </form>
      {area && <div id="observations"><AreaObservations areaId={areaId} /></div>}
    </section>
  )
}

/** The area's own observations, newest first: the kind, when, who, the word, the photo, and whether a job follows it. A planner names the job here. */
function AreaObservations({ areaId }: { areaId: string }) {
  const { t } = useTranslation()
  const { site } = useClub()
  const rows = useAreaObservations(areaId)
  const nameOf = usePersonNames()
  const plan = useWriteAccess('job.plan')
  if (!rows) return null
  return (
    <Section title={t('observe.recent')} testId="observe-list">
      {rows.length === 0 && <p className="opacity-70" data-testid="observe-none">{t('observe.recentNone')}</p>}
      <ul className="flex flex-col gap-2">
        {rows.map((o) => (
          <li key={o.id} className="card flex flex-col gap-1" data-testid="observe-row" data-id={o.id} data-kind={o.kind} data-linked={o.job_id ? '1' : '0'}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="font-semibold">{kindText(t, o.kind)}</span>
              {o.job_id ? <Pill tone="green">{t('observe.followed')}</Pill> : <Pill tone="amber">{t('observe.noFollowUp')}</Pill>}
            </div>
            {o.note && <p data-testid="observe-row-note">{o.note}</p>}
            <p className="meta">{formatLocal(o.observed_at, site.timezone)}{nameOf(o.actor_person_id) ? ` · ${t('measures.by', { who: nameOf(o.actor_person_id) })}` : ''}{o._pending ? ` · ${t('measures.pill.pending')}` : ''}</p>
            <PhotoStrip entityId={o.id} />
            {plan.permitted && !o.job_id && !o._pending && <FollowUp o={o} />}
          </li>
        ))}
      </ul>
    </Section>
  )
}

/** The follow-up of one observation (rule 1): an ordinary job of the plan, created in the planner, then named here once (observation.link_job@1, an intent
 *  of the office: the server's verdict is shown where the planner tapped; offline the link is kept on the device and goes at the next sync). */
function FollowUp({ o }: { o: Observation }) {
  const { t, i18n } = useTranslation()
  const jobs = useJobs(); const jobAreas = useJobAreas()
  const act = useWorkshopAction()
  const [open, setOpen] = useState(false)
  const [jobId, setJobId] = useState('')
  const candidates = useMemo(() => candidateJobs(jobs ?? [], jobAreas ?? [], o.area_id, o.observed_local_date, (j) => say(j.title, i18n.language) || j.task_type_code), [jobs, jobAreas, o.area_id, o.observed_local_date, i18n.language])
  if (act.ok) return <Banner tone="ok"><span data-testid="observe-linked">{act.ok}</span></Banner>
  if (act.pending) return <PendingBanner testId="observe-link-pending" />
  return (
    <div className="flex flex-col gap-2">
      {!open ? (
        <button type="button" className="btn !px-4" onClick={() => setOpen(true)} data-testid="observe-link-open">{t('observe.followUp.title')}</button>
      ) : (
        <form className="flex flex-col gap-3 border-t pt-3" data-testid="observe-link-form" onSubmit={(e) => { e.preventDefault(); if (jobId) void act.run(LINK_JOB, { observation_id: o.id, job_id: jobId }, 'observe.followUp.linked') }}>
          <p className="meta">{t('observe.followUp.help')}</p>
          <Link to="/plan/new" search={{ area: o.area_id }} className="btn" data-testid="observe-create-job">{t('observe.followUp.create')}</Link>
          {candidates.length === 0 ? <p className="meta" data-testid="observe-link-none">{t('observe.followUp.none')}</p> : (
            <Field label={t('observe.followUp.pick')} htmlFor={`link-${o.id}`}>
              <select id={`link-${o.id}`} className="input" value={jobId} onChange={(e) => setJobId(e.target.value)} data-testid="observe-link-job">
                <option value="">{t('observe.areaPick')}</option>
                {candidates.map((j) => <option key={j.id} value={j.id}>{`${j.date} · ${j.title}${j.onArea ? ` · ${t('observe.followUp.onArea')}` : ''}`}</option>)}
              </select>
            </Field>
          )}
          {act.error && <Banner tone="error"><span data-testid="observe-link-error">{act.error}</span></Banner>}
          <button type="submit" className="btn btn-primary" disabled={act.busy || !jobId} data-testid="observe-link">{act.busy ? t('common.saving') : t('observe.followUp.link')}</button>
        </form>
      )}
    </div>
  )
}
